import {
  OrganizationMembershipsProfileFieldFilter,
  OrganizationMembershipsProfileFieldStatus,
} from "@/admin/community/members/__generated__/CommunityMembersListPagePaginationQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { MemberGroupFilterInput } from "@/organization/people/actions/message/__generated__/MessageProductMembersButtonQuery.graphql"
import MembersSortDropdown, {
  MembersSortDropdownProps,
} from "@/organization/people/page/filters/MembersSortDropdown"
import { ProductMembersListOrderByField } from "@/product/member/common/list/ProductMembersList"
import ProductMembersListPageGroupFilter from "@/product/member/page/filters/ProductMembersListPageGroupFilter"
import ProductMembersListPageRoleFilter, {
  ProductMembersListPageRoleFilterState,
} from "@/product/member/page/filters/ProductMembersListPageRoleFilter"
import ProductMembershipsExportCsvButton from "@/product/member/page/ProductMembershipsExportCsvButton"
import ProductMembersListPageFilterTags from "@/product/member/page/ProductMembersListPageFilterTags"
import { ProductMembersListToolbarState } from "@/product/member/page/useProductMembersListToolbarState"
import { ProductMembersListPageToolbarFragment$key } from "@/product/member/page/__generated__/ProductMembersListPageToolbarFragment.graphql"
import useIsWebView from "@/product/util/hook/useIsWebView"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import MembersProfileFieldFilterDropdown from "@components/dropdown/profile-field/MembersProfileFieldFilterDropdown"
import {
  DiscoButton,
  DiscoButtonSkeleton,
  DiscoDropdown,
  DiscoIcon,
  DiscoText,
} from "@disco-ui"
import { OverridableDiscoButton } from "@disco-ui/button/OverridableDiscoButton"
import DiscoPaginatedDropdown from "@disco-ui/dropdown/DiscoPaginatedDropdown"
import DiscoTableSearchInput, {
  DiscoTableSearchInputSkeleton,
} from "@disco-ui/table/header/search/DiscoTableSearchInput"
import { DiscoTableSortDropdownSkeleton } from "@disco-ui/table/header/search/DiscoTableSortDropdown"
import { useTheme } from "@material-ui/core"
import { observer } from "mobx-react-lite"
import React from "react"
import { graphql, useFragment } from "react-relay"

interface ProductMembersListPageToolbarProps {
  productKey: ProductMembersListPageToolbarFragment$key
  toolbarState: ProductMembersListToolbarState
  setToolbarState: React.Dispatch<React.SetStateAction<ProductMembersListToolbarState>>
  filterButtonVariant?: "default" | "icon"
  sortButtonVariant?: "default" | "icon"
  showExportCsvButton?: boolean
  sortOptions: MembersSortDropdownProps["options"]
}

function ProductMembersListPageToolbar({
  productKey,
  toolbarState,
  setToolbarState,
  filterButtonVariant = "default",
  sortButtonVariant = "default",
  showExportCsvButton,
  sortOptions,
}: ProductMembersListPageToolbarProps) {
  const theme = useTheme()
  const classes = useStyles()
  const activeOrganization = useActiveOrganization()!

  const product = useFragment<ProductMembersListPageToolbarFragment$key>(
    graphql`
      fragment ProductMembersListPageToolbarFragment on Product {
        id
        type
        ...ProductMembershipsExportCsvButtonFragment
      }
    `,
    productKey
  )

  const isWebView = useIsWebView()
  const isPathway = product.type === "pathway"

  const cannotReadPrivateProfileFields =
    !activeOrganization.viewerPermissions.has("members.read_private")

  return (
    <div className={classes.container}>
      <div className={classes.toolsContainer}>
        <div className={classes.lhsHeader}>
          {!isWebView && (
            <DiscoTableSearchInput
              testid={"ProductMembersListPageToolbar"}
              onChange={handleSearchChange}
              backgroundColor={
                theme.palette.type === "dark"
                  ? theme.palette.groovy.onDark[600]
                  : undefined
              }
              placeholder={`Search`}
            />
          )}
        </div>

        <div className={classes.rhsHeader}>
          <DiscoDropdown
            testid={"ProductMembersListPageToolbar.filter-select"}
            menuClasses={{
              paper: classes.filterDropdownMenu,
            }}
            menuButton={({ rightIcon, ...btnProps }) => {
              const testid = "ProductMembersListPageToolbar.filter-select.header-button"
              if (filterButtonVariant === "icon") {
                return (
                  <OverridableDiscoButton
                    {...btnProps}
                    testid={testid}
                    width={"40px"}
                    noPadding
                  >
                    <DiscoIcon icon={"iconsax.sort"} height={24} width={24} />
                  </OverridableDiscoButton>
                )
              }

              return (
                <DiscoButton
                  {...btnProps}
                  rightIcon={rightIcon}
                  data-testid={testid}
                  className={classes.dropdownButton}
                >
                  {"Filters"}
                </DiscoButton>
              )
            }}
          >
            <DiscoText variant={"body-xs-500"} marginTop={0.5} marginBottom={0.5}>
              {"Filter by..."}
            </DiscoText>

            {/* Roles dropdown */}
            {!isPathway && hasSelectedRole(toolbarState) && (
              <ProductMembersListPageRoleFilter
                filterState={toolbarState}
                setFilterState={(newToolbarState) =>
                  setToolbarState({
                    ...toolbarState,
                    ...newToolbarState,
                  })
                }
              />
            )}

            {/* Groups dropdown */}
            <DiscoPaginatedDropdown
              testid={"ProductMembersListPageGroupFilter.group-select"}
              title={"Groups"}
              icon={"group-share"}
              content={
                <ProductMembersListPageGroupFilter
                  filterState={toolbarState}
                  setFilterState={(newToolbarState) => {
                    setToolbarState({
                      ...toolbarState,
                      ...newToolbarState,
                    })
                  }}
                />
              }
            />

            {/* Profile Fields dropdowns */}
            <MembersProfileFieldFilterDropdown
              showDefaultOptions={false}
              isPrivate={cannotReadPrivateProfileFields ? false : null}
              webFormQuestionTypes={["single_select", "multiple_select"]}
              filterState={toolbarState}
              setFilterState={(newFilterState) =>
                setToolbarState({
                  ...toolbarState,
                  ...newFilterState,
                })
              }
            />
          </DiscoDropdown>

          {/* Sort */}
          <MembersSortDropdown
            sortState={toolbarState}
            setSortState={(newSortState) =>
              setToolbarState({
                ...toolbarState,
                ...newSortState,
              })
            }
            buttonVariant={sortButtonVariant}
            options={sortOptions}
          />

          {/* Export CSV */}
          {showExportCsvButton && (
            <ProductMembershipsExportCsvButton
              productKey={product}
              toolbarState={toolbarState}
            />
          )}
        </div>
      </div>

      <div className={classes.filterTagsContainer}>
        <ProductMembersListPageFilterTags
          toolbarState={toolbarState}
          setToolbarState={setToolbarState}
        />
      </div>
    </div>
  )

  function handleSearchChange(value: string) {
    setToolbarState({
      ...toolbarState,
      search: value,
    })
  }
}

export function mapOrderByFieldToColumn(field: string): ProductMembersListOrderByField {
  const fieldNameMap: Record<string, ProductMembersListOrderByField> = {
    member_name: "name",
    recently_active: "last_activity_at",
    joined_on: "creation_datetime",
    role: "role",
  }

  const name = fieldNameMap[field]

  if (!name) {
    throw new Error(
      `[DEV ERROR] Field: "${field}" not mapped to a ProductMembersListOrderByField. Check if it's supported first as an orderByField before adding it here!`
    )
  }

  return name
}

export function mapSelectedProfileFieldsToInput(
  toolbarState: ProductMembersListToolbarState
) {
  const profileFieldsFilters: OrganizationMembershipsProfileFieldFilter[] = []
  for (const filter of Object.values(toolbarState.profileFields)) {
    if (!filter?.value) continue
    const valueIsStatus = ["responded", "not_responded"].includes(filter.value)
    profileFieldsFilters.push({
      id: filter.fieldId,
      status: valueIsStatus
        ? (filter.value as OrganizationMembershipsProfileFieldStatus)
        : null,
      optionId: valueIsStatus ? null : filter.value,
    })
  }
  return profileFieldsFilters
}

export function mapSelectedGroupFilterToInput(
  toolbarState: ProductMembersListToolbarState
) {
  const memberGroupFilterInput: MemberGroupFilterInput = {}

  const { selectedGroup } = toolbarState

  if (selectedGroup.value === "unassigned") {
    memberGroupFilterInput.unassigned = true
  } else if (selectedGroup.value !== "all") {
    memberGroupFilterInput.memberGroupId = selectedGroup.value
  }
  return memberGroupFilterInput
}

function hasSelectedRole(
  toolbarState: any
): toolbarState is ProductMembersListPageRoleFilterState {
  return "selectedRole" in toolbarState
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    width: "100%",
  },
  toolsContainer: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    width: "100%",
  },
  lhsHeader: {
    flexGrow: 1,
    minWidth: "240px",
  },
  rhsHeader: {
    display: "flex",
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
  },
  filterTagsContainer: {
    marginTop: theme.spacing(1.5),
  },

  filterDropdownMenu: {
    minWidth: "200px",
  },
  dropdownButton: {
    minWidth: "140px",
    justifyContent: "space-between",
  },
}))

export const ProductMembersListPageToolbarSkeleton: React.FC = () => {
  const classes = useStyles()

  return (
    <div className={classes.toolsContainer}>
      <div className={classes.lhsHeader}>
        <DiscoTableSearchInputSkeleton />
      </div>
      <div className={classes.rhsHeader}>
        <DiscoTableSortDropdownSkeleton />
        <DiscoTableSortDropdownSkeleton />
        <DiscoButtonSkeleton width={"150px"} />
      </div>
    </div>
  )
}

export default Relay.withSkeleton<ProductMembersListPageToolbarProps>({
  component: observer(ProductMembersListPageToolbar),
  skeleton: ProductMembersListPageToolbarSkeleton,
})
